import { useId, useState, ReactNode } from 'react';
import styles from './contentSamplingPlayer.module.scss';
import cn from 'classnames';
import { IconSkipBackward } from '@/assets/icons/IconSkipBackwards';
import { IconPlayButton } from '@/assets/icons/IconPlayButton';
import { IconPauseButton } from '@/assets/icons/IconPauseButton';
import { IconTranscript } from '@/assets/icons/IconTranscript';
import { IconSkipForward } from '@/assets/icons/IconSkipForward';
import { IconReplayButton } from '@/assets/icons/IconReplayButton';
import { Modal } from '../Modal/Modal';
import { Button } from '@/ui-components/atoms/Button/Button';
import { useAudioPlayer } from '@/hooks/useAudioPlayer';

export interface SamplingModalProps {
    modalBody?: ReactNode;
}

export interface AudioPlayerProps {
    source: string;
    name?: string;
    isActive: boolean;
    onActivate: (isActive: boolean) => void;
    isContentSampler?: boolean;
    modal?: SamplingModalProps;
    isLightTheme?: boolean;
}

export function ContentSamplingPlayer({
    source,
    name = '',
    isActive,
    onActivate,
    modal,
    isLightTheme = false,
}: AudioPlayerProps): React.JSX.Element {
    const {
        setAudioElementRef,
        isEnded,
        duration,
        progressPercent,
        time,
        showPlayButton,
        showPauseButton,
        formatTime,
        handlePlayPause,
        handleReplay,
        handleScrub,
        handleAudioEnded,
        handleTimeUpdate,
        handleLoadedMetadata,
        skip,
    } = useAudioPlayer({ source, isActive, onActivate });

    const [isModalOpen, setModelOpen] = useState(false);

    const inputId = useId();

    const analytics: DataLayerInfo = {
        audioInfo: {
            name: name || source.split('/').pop() || '',
        },
    };

    return (
        <div className={styles.audioContainer}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption -- Captions require a CMS-provided VTT file; this component provides a transcript modal as an alternative. */}
            <audio
                src={source}
                ref={setAudioElementRef}
                onEnded={handleAudioEnded}
                onLoadedMetadata={handleLoadedMetadata}
                onTimeUpdate={handleTimeUpdate}
                data-audioplayer={analytics.audioInfo.name}
            />
            <div className={cn(styles.controls, { [styles.active]: isActive })}>
                <div className={styles.slideAnimation}>
                    <div className={isLightTheme ? styles.lightProgressBarWrapper : styles.progressBarWrapper}>
                        <input
                            type="range"
                            id={inputId}
                            min={0}
                            max={duration || 0}
                            step="any"
                            value={time.currentTime ?? 0}
                            onChange={handleScrub}
                            aria-valuetext="Audio bar slider"
                            aria-label="Audio bar slider"
                        />
                        <div
                            className={styles.progressBar}
                            style={{
                                background: `linear-gradient(to right, var(--range-progress-bar-ellapsed) ${progressPercent}%, var(--range-progress-bar-remaining) ${progressPercent}%)`,
                            }}
                        />
                    </div>
                    <div className={styles.timeDisplayWrapper}>
                        <time
                            className={styles.timeEllapsed}
                            aria-label={`time ellapsed ${formatTime(time.currentTime)}`}
                        >
                            {formatTime(time.currentTime)}
                        </time>
                        <time
                            className={styles.timeRemaining}
                            aria-label={`time remaining ${formatTime(time.timeRemaining)}`}
                        >
                            {`-${formatTime(time.timeRemaining)}`}
                        </time>
                    </div>
                </div>
                <div className={isLightTheme ? styles.lightThemePlayerControls : styles.controlsWrapperStyles}>
                    <button onClick={() => skip('back')} aria-label={`skip backward ${name}`} data-donottrack>
                        <IconSkipBackward fill={isLightTheme ? 'black' : 'white'} />
                    </button>
                    {showPlayButton && (
                        <button
                            onClick={handlePlayPause}
                            aria-label={`play ${name}`}
                            className={styles.playButton}
                            data-donottrack
                        >
                            <IconPlayButton fill={isLightTheme ? 'white' : 'black'} />
                        </button>
                    )}
                    {showPauseButton && (
                        <button
                            onClick={handlePlayPause}
                            aria-label={`pause ${name}`}
                            className={styles.pauseButton}
                            data-donottrack
                        >
                            <IconPauseButton fill={isLightTheme ? 'white' : 'black'} />
                        </button>
                    )}
                    {isEnded && (
                        <button
                            onClick={handleReplay}
                            aria-label={`replay ${name}`}
                            className={styles.replayButton}
                            data-donottrack
                        >
                            <IconReplayButton fill={isLightTheme ? 'black' : 'white'} />
                        </button>
                    )}
                    <button onClick={() => skip('forward')} aria-label={`skip forward ${name}`} data-donottrack>
                        <IconSkipForward fill={isLightTheme ? 'black' : 'white'} />
                    </button>
                    {modal && (
                        <button
                            onClick={() => setModelOpen(true)}
                            aria-label={`open transcript ${name}`}
                            className={styles.transcriptIcon}
                            data-donottrack
                        >
                            <IconTranscript fill={isLightTheme ? 'black' : 'white'} />
                        </button>
                    )}
                </div>
            </div>
            {modal && (
                <Modal variant={'small'} open={isModalOpen} onClose={() => setModelOpen(false)} centeredLayout={true}>
                    {modal?.modalBody}
                    <Button
                        className={styles.closeButton}
                        variant={'secondary'}
                        type="button"
                        size="large"
                        ariaLabel={'Close'}
                        onClick={() => setModelOpen(false)}
                    >
                        {'Close'}
                    </Button>
                </Modal>
            )}
        </div>
    );
}
